import { ExpandLess, ExpandMore } from "@mui/icons-material";
import BarChartRoundedIcon from "@mui/icons-material/BarChartRounded";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import CurrencyExchangeIcon from "@mui/icons-material/CurrencyExchange";
import GridViewRoundedIcon from "@mui/icons-material/GridViewRounded";
import HelpRoundedIcon from "@mui/icons-material/HelpRounded";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import InfoRoundedIcon from "@mui/icons-material/InfoRounded";
import PeopleRoundedIcon from "@mui/icons-material/PeopleRounded";
import SettingsRoundedIcon from "@mui/icons-material/SettingsRounded";
import TaskIcon from "@mui/icons-material/Task";
import TipsAndUpdatesIcon from "@mui/icons-material/TipsAndUpdates";
import Collapse from "@mui/material/Collapse";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Stack from "@mui/material/Stack";
import { alpha } from "@mui/material/styles"; // Import alpha for translucent colors
import Tooltip from "@mui/material/Tooltip";
import * as React from "react";
import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AuthContext } from "../../../../App";
import QueueIcon from '@mui/icons-material/Queue';
import CopyrightIcon from '@mui/icons-material/Copyright';
import ChangeCircleIcon from "@mui/icons-material/ChangeCircle";

import {
  ADMIN,
  ASSETS,
  COMPONENT_HIERARCHY,
  DASHBOARD,
  getPermissionsForComponent,
  HOME,
  isEmpty,
  REPORTS,
  ROLE_BASED_ROUTES,
  SETTINGS,
  SOLUTIONS,
  TASK,
  QUEUE,
  TICKETS,
  CI,
  CHANGE
} from "../../../../Constants/RolesConstants";
import FeedbackPopup from "../../home/Feedback";

const secondaryListItems = ["About", "Feedback"];

const SIDE_BAR_COMPONENTS = {
  [HOME]: {
    text: "Home",
    icon: <HomeRoundedIcon />,
    link: "/home",
  },
  [DASHBOARD]: {
    text: "Dashboard",
    icon: <GridViewRoundedIcon />,
    link: "/dashboard",
  },
  [TICKETS]: {
    text: "Tickets",
    icon: <ConfirmationNumberIcon />,
    link: "/tickets",
  },
  [SOLUTIONS]: {
    text: "Solutions",
    icon: <TipsAndUpdatesIcon />,
    link: "/solutions",
  },
  [ASSETS]: {
    text: "Assets",
    icon: <CurrencyExchangeIcon />,
    link: "/assets",
  },
  [CI]: {
    text: "CMDB",
    icon: <CopyrightIcon />,
    link: "/configItem",
  },
  [REPORTS]: {
    text: "Reports",
    icon: <BarChartRoundedIcon />,
    link: "/reports",
  },
  [ADMIN]: {
    text: "Administration",
    icon: <PeopleRoundedIcon />,
    link: "/admin",
  },
  [SETTINGS]: {
    text: "Settings",
    icon: <SettingsRoundedIcon />,
    link: "/settings",
  },
  [TASK]: {
    text: "Tasks",
    icon: <TaskIcon />,
    link: "/tasks",
  },
  [CHANGE]: {
    text: "Change",
    icon: <ChangeCircleIcon />,
    link: "/change",
  },
  [QUEUE]: {
    text: "Queue",
    icon: <QueueIcon />,
    link: "/queue",
  },
  About: {
    text: "About",
    icon: <InfoRoundedIcon />,
    link: "/about",
  },
  Feedback: {
    text: "Feedback",
    icon: <HelpRoundedIcon />,
    isFeedback: true,
  },
};

export default function MenuContent({ isCollapsed }) {
  const { userRole } = React.useContext(AuthContext);
  const menuItemsForRole = ROLE_BASED_ROUTES[userRole] || [];

  const [open, setOpen] = useState({});
  const location = useLocation();
  const [isFeedbackOpen, setFeedbackOpen] = useState(false); // State for feedback popup

  const handleToggle = (key, item) => {
    if (item.isFeedback) {
      setFeedbackOpen(true);
    } else if (
      !isCollapsed &&
      item.subListItems &&
      item.subListItems.length > 0
    ) {
      setOpen((prevState) => ({
        ...prevState,
        [key]: !prevState[key],
      }));
    }
  };

  const getItemsView = (comps, level) => {
    if (!comps || comps.length === 0) {
      return;
    }

    if (level === "main") {
      comps = comps.filter((comp) => {
        const subComps = COMPONENT_HIERARCHY[comp] || [];
        const permissions = getPermissionsForComponent(comp);
        return subComps.some(
          (subComp) =>
            isEmpty(permissions[subComp]) ||
            permissions[subComp]?.canViewMenu ||
            permissions[subComp]?.canView
        );
      });
    }

    return comps.map((componentName, index) => {
      const item = SIDE_BAR_COMPONENTS[componentName];
      if (!item) {
        return null;
      }
      const itemKey = item.text || `item-${index}`; // Use item.text or a fallback for key
      const isSelected =
        (location.pathname.includes("editTicket")
          ? "/tickets"
          : location.pathname) === item.link;

      const icon = (
        <ListItemIcon
          sx={(theme) => ({
            minWidth: 0,
            mr: isCollapsed ? "auto" : 0,
            justifyContent: "center",
            color: theme.palette.text.secondary,
            border: `1px solid rgb(221, 221, 221)`,
            borderRadius: "50%", // Make it circular
            padding: theme.spacing(1), // Adjust padding if needed for circle
            boxSizing: "border-box",
            display: "flex",
            alignItems: "center",
            "&:hover": {
              borderColor: theme.palette.primary.main,
              backgroundColor: alpha(theme.palette.primary.main, 0.15),
              "& > svg": {
                color: theme.palette.primary.main,
              },
            },
          })}
        >
          {item.icon}
        </ListItemIcon>
      );

      return (
        <React.Fragment key={itemKey}>
          <ListItem
            disablePadding
            sx={{
              display: "block",
            }}
          >
            {" "}
            {/*add link to their specific navigation*/}{" "}
            <ListItemButton
              onClick={() => handleToggle(itemKey, item)}
              component={item.link ? Link : "li"}
              to={item.link}
              sx={(theme) => ({
                minHeight: 48,
                justifyContent: isCollapsed ? "center" : "initial",
                px: 2.5,
                ...(isSelected && {
                  ...(!isCollapsed && {
                    backgroundColor: alpha(theme.palette.primary.main, 0.25),
                    color: alpha(theme.palette.primary.main, 1), // Selected text color
                  }),
                  "& .MuiListItemIcon-root": {
                    borderColor: theme.palette.primary.main,
                    backgroundColor: alpha(theme.palette.primary.main, 0.25),
                    "& > svg": {
                      color: theme.palette.primary.main,
                    },
                  },
                }),
                "&:hover": isCollapsed
                  ? {}
                  : {
                      borderColor: theme.palette.primary.main,
                      backgroundColor: alpha(theme.palette.primary.main, 0.25),
                      color: alpha(theme.palette.primary.main, 1),
                      "& .MuiListItemIcon-root": {
                        borderColor: theme.palette.primary.main,
                        "& > svg": {
                          color: theme.palette.primary.main,
                        },
                      },
                    },
              })}
            >
              {isCollapsed ? (
                <Tooltip title={item.text} placement="right">
                  {icon}
                </Tooltip>
              ) : (
                icon
              )}
              <ListItemText
                primary={item.text}
                sx={{
                  opacity: isCollapsed ? 0 : 1,
                  display: isCollapsed ? "none" : "block", // Ensures it's removed from layout
                  // whiteSpace: "nowrap", // Prevents text wrapping
                }}
              />{" "}
              {!isCollapsed &&
                getMoreOrLessIcon(item.subListItems, open[itemKey])}{" "}
            </ListItemButton>{" "}
          </ListItem>{" "}
          {!isCollapsed &&
            getSubListItemsView(
              item.subListItems,
              open[itemKey],
              isSelected
            )}{" "}
        </React.Fragment>
      );
    });
  };

  return (
    <React.Fragment>
      <Stack
        sx={{
          flexGrow: 1,
          p: 1,
          justifyContent: "space-between",
        }}
      >
        <List dense> {getItemsView(menuItemsForRole, "main")} </List>
        <List dense>
          {" "}
          {getItemsView(secondaryListItems, "secondary")}{" "}
        </List>{" "}
      </Stack>{" "}
      <FeedbackPopup
        open={isFeedbackOpen}
        onClose={() => setFeedbackOpen(false)}
      />{" "}
    </React.Fragment>
  );

  function getMoreOrLessIcon(subList, isOpened = false) {
    if (!subList || subList.length === 0 || isCollapsed) {
      return null;
    }
    return isOpened ? <ExpandLess /> : <ExpandMore />;
  }

  function getSubListItemsView(subList, isOpened = false) {
    if (!subList || subList.length === 0 || isCollapsed) {
      return null;
    }

    return (
      <Collapse in={isOpened} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>
          {" "}
          {subList.map((item, subIndex) => (
            <ListItem
              key={subIndex}
              disablePadding
              sx={{
                pl: 4,
              }}
            >
              <ListItemButton
                component={item.link ? Link : "li"}
                to={item.link}
                sx={{
                  pl: 4,
                }} // Keep padding for sub-items
              >
                <ListItemIcon
                  sx={(theme) => ({
                    minWidth: 0,
                    justifyContent: "center",
                    border: `1px solid rgb(221, 221, 221)`,
                    borderRadius: "50%", // Make it circular
                    padding: theme.spacing(1), // Adjust padding if needed
                    boxSizing: "border-box",
                    display: "flex",
                    alignItems: "center",
                    "&:hover": {
                      borderColor: theme.palette.primary.main,
                      backgroundColor: alpha(theme.palette.primary.main, 0.08),
                      "& > svg": {
                        color: theme.palette.primary.main,
                      },
                    },
                  })}
                >
                  {item.icon}{" "}
                </ListItemIcon>{" "}
                <ListItemText
                  primary={item.text}
                  sx={{
                    opacity: isCollapsed ? 0 : 1,
                    display: isCollapsed ? "none" : "block", // Ensures it's removed from layout
                    // whiteSpace: "nowrap", // Prevents text wrapping
                  }}
                />{" "}
              </ListItemButton>{" "}
            </ListItem>
          ))}{" "}
        </List>{" "}
      </Collapse>
    );
  }
}
